import { Button, Col, Pagination, Row, Space, Spin, Table } from 'antd';
import React, { useState } from 'react';
import { DeleteOutlined, PlusCircleOutlined, EditOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import PopConfirm from '../../../shared/Components/PopConfirm';
import { useMutation } from '@apollo/client';
import { useSortedBasicTaxiPricingQuery } from './hooks/useSortedBasicTaxiPricingQuery';
import BasicTaxiPricingCreateModal from './BasicTaxiPricingCreateModal';
import {
    DeleteBasicTaxiPricingRequest,
    DeleteBasicTaxiPricingResponse,
    CITRIN_DELETE_BASIC_PRICING_TAXI,
} from '../../../GraphQL';
import { BasicPricingTaxi } from '../../../shared/types';
import { useCurrentBasicPricingTaxi } from './hooks/useCurrentBasicPricingTaxi';
import BasicTaxiPricingEditModal from './BasicTaxiPricingEditModal';

const BasicTaxiDistancePricingTable = () => {
    const [isCreateModalVisible, setCreateModalVisible] = useState(false);
    const [isEditModalVisible, setEditModalVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { setCurrentBasicPricingTaxi } = useCurrentBasicPricingTaxi();

    const { sortedPricings: basicTaxipricings, refetch, loading, ...pagination } = useSortedBasicTaxiPricingQuery();
    const [deleteMutation] = useMutation<DeleteBasicTaxiPricingResponse, DeleteBasicTaxiPricingRequest>(
        CITRIN_DELETE_BASIC_PRICING_TAXI
    );

    const handleCreateBasicPricing = () => {
        setCreateModalVisible(true);
    };
    const handleEditBasicPricing = (pricing: BasicPricingTaxi) => {
        setCurrentBasicPricingTaxi(pricing);
        setEditModalVisible(true);
    };

    const handleRemoveBasicPricing = (basicPricingTaxi: BasicPricingTaxi) => {
        setIsLoading(true);
        deleteMutation({ variables: { id: basicPricingTaxi._id } })
            .then(() => {
                refetch();
            })
            .catch((err) => console.error(err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const columns = [
        {
            title: 'Range Name',
            dataIndex: 'rangeName',
            key: 'rangeName',
        },
        {
            title: 'Min. Distance (km)',
            dataIndex: 'minDistance',
            key: 'minDistance',
        },
        {
            title: 'Max. Distance (km)',
            dataIndex: 'maxDistance',
            key: 'maxDistance',
            render: (value: number | null) => (value === null ? '∞' : value),
        },
        {
            title: 'Price per km (TND)',
            dataIndex: 'pricePerKm',
            key: 'pricePerKm',
            render: (value: number) => value.toFixed(3),
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, basicPricingTaxi) => (
                <Space>
                    <a href="#" onClick={() => handleEditBasicPricing(basicPricingTaxi)}>
                        <EditOutlined style={{ fontSize: '16px', color: 'green' }} />
                    </a>
                    <PopConfirm
                        title={'Delete this range'}
                        description={'Are you sure to delete this pricing range?'}
                        onConfirmTask={() => handleRemoveBasicPricing(basicPricingTaxi)}
                        popIcon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                        actionIcon={<DeleteOutlined style={{ color: 'red', fontSize: 16 }} />}
                    />
                </Space>
            ),
        },
    ];

    const Paginator = () => (
        <Row justify={'center'}>
            <Col>
                <Pagination
                    size="default"
                    total={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    current={pagination.page}
                    showTotal={(total) => `Total Basic Taxi-mobility Prices ${total}`}
                    onChange={(newPage) => pagination.setPage(newPage)}
                    showQuickJumper={true}
                    showSizeChanger={false}
                    showLessItems={false}
                />
            </Col>
        </Row>
    );

    return (
        <>
            {isCreateModalVisible && (
                <BasicTaxiPricingCreateModal
                    updateList={refetch}
                    isOpen={isCreateModalVisible}
                    onRequestClose={setCreateModalVisible}
                />
            )}
            {isEditModalVisible && (
                <BasicTaxiPricingEditModal
                    updateList={refetch}
                    isOpen={isEditModalVisible}
                    onRequestClose={setEditModalVisible}
                />
            )}
            <Row justify={'end'} style={{ marginBottom: 20 }}>
                <Button type="primary" shape="round" icon={<PlusCircleOutlined />} onClick={handleCreateBasicPricing}>
                    Add Distance Range
                </Button>
            </Row>

            <Row gutter={16} style={{ paddingBottom: 10, paddingTop: 10 }}>
                <Col xs={24}>
                    <Spin spinning={isLoading || loading}>
                        <Table
                            dataSource={basicTaxipricings}
                            columns={columns}
                            pagination={false}
                            rowKey={() => Math.random().toString()}
                            scroll={{ x: 1500 }}
                        ></Table>
                    </Spin>
                </Col>
            </Row>
            <Paginator />
        </>
    );
};

export default BasicTaxiDistancePricingTable;
